export type DeviceKind = "audioinput" | "videoinput" | "audiooutput";

export type DeviceList = {
  microphones: MediaDeviceInfo[];
  cameras: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];

  labelled: boolean;
};

const EMPTY: DeviceList = { microphones: [], cameras: [], speakers: [], labelled: false };

const KEY = "bsmeet.devices";

export type DevicePreferences = {
  microphoneId: string | null;
  cameraId: string | null;
  speakerId: string | null;

  cameraEffect: string | null;
};

const NO_PREFERENCE: DevicePreferences = {
  microphoneId: null,
  cameraId: null,
  speakerId: null,
  cameraEffect: null,
};

export function loadPreferences(): DevicePreferences {
  try {
    const raw = globalThis.localStorage.getItem(KEY);
    if (raw === null) return NO_PREFERENCE;
    return { ...NO_PREFERENCE, ...(JSON.parse(raw) as Partial<DevicePreferences>) };
  } catch {
    return NO_PREFERENCE;
  }
}

export function savePreferences(preferences: DevicePreferences): void {
  try {
    globalThis.localStorage.setItem(KEY, JSON.stringify(preferences));
  } catch (error) {
    console.error("device preference save failed:", error);
  }
}

let current: DevicePreferences | null = null;
const listeners = new Set<() => void>();

export function getPreferences(): DevicePreferences {
  current ??= loadPreferences();
  return current;
}

export function setPreference(kind: keyof DevicePreferences, deviceId: string): void {
  const next = { ...getPreferences(), [kind]: deviceId };
  current = next;
  savePreferences(next);
  for (const listener of listeners) listener();
}

export function subscribePreferences(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function nextDevice(
  list: MediaDeviceInfo[],
  currentId: string,
): { device: MediaDeviceInfo; index: number } | undefined {
  if (list.length < 2) return undefined;
  const index = (list.findIndex((device) => device.deviceId === currentId) + 1) % list.length;
  const device = list[index];
  return device === undefined ? undefined : { device, index };
}

export type OutputKind = "earpiece" | "headset" | "bluetooth" | "speaker";

export function outputKind(device: MediaDeviceInfo | undefined): OutputKind {
  const label = device?.label.toLowerCase() ?? "";
  if (/earpiece|receiver|handset/u.test(label)) return "earpiece";
  if (/bluetooth|hands-free|airpod|\bbt\b/u.test(label)) return "bluetooth";
  if (/headphone|headset|earbud/u.test(label)) return "headset";
  return "speaker";
}

export function resolveDeviceId(list: MediaDeviceInfo[], preferred: string | null): string {
  if (preferred !== null && list.some((device) => device.deviceId === preferred)) return preferred;
  return list[0]?.deviceId ?? "";
}

export function describeDevice(device: MediaDeviceInfo, fallback: string, index: number): string {
  return device.label === "" ? `${fallback} ${index + 1}` : device.label;
}

export function deviceOptions(
  list: MediaDeviceInfo[],
  fallback: string,
): { value: string; label: string }[] {
  if (list.length === 0) return [{ value: "", label: fallback }];
  return list.map((device, index) => ({
    value: device.deviceId,
    label: describeDevice(device, fallback, index),
  }));
}

function isPseudoDevice(device: MediaDeviceInfo): boolean {
  return device.deviceId === "default" || device.deviceId === "communications";
}

function dedupeByGroup(list: MediaDeviceInfo[]): MediaDeviceInfo[] {
  const byGroup = new Map<string, MediaDeviceInfo>();
  const ungrouped: MediaDeviceInfo[] = [];
  for (const device of list) {
    if (device.groupId === "") {
      ungrouped.push(device);
      continue;
    }
    const existing = byGroup.get(device.groupId);
    if (existing === undefined || (isPseudoDevice(existing) && !isPseudoDevice(device))) {
      byGroup.set(device.groupId, device);
    }
  }
  return [...byGroup.values(), ...ungrouped];
}

export async function enumerate(): Promise<DeviceList> {
  if (globalThis.navigator?.mediaDevices === undefined) return EMPTY;
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const pick = (kind: DeviceKind) => devices.filter((d) => d.kind === kind);
    return {
      microphones: dedupeByGroup(pick("audioinput")),
      cameras: pick("videoinput"),
      speakers: dedupeByGroup(pick("audiooutput")),
      labelled: devices.some((d) => d.label !== ""),
    };
  } catch {
    return EMPTY;
  }
}

export function onDeviceChange(handler: () => void): () => void {
  const target = globalThis.navigator?.mediaDevices;
  if (target === undefined) return () => undefined;
  target.addEventListener("devicechange", handler);
  return () => {
    target.removeEventListener("devicechange", handler);
  };
}

export function canChooseSpeaker(): boolean {
  return typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;
}

type WithSinkId = { setSinkId?: (id: string) => Promise<void> };

export async function applySpeaker(
  element: HTMLMediaElement,
  speakerId: string | null,
): Promise<void> {
  if (speakerId === null || !canChooseSpeaker()) return;
  try {
    await (element as HTMLMediaElement & WithSinkId).setSinkId?.(speakerId);
  } catch (error) {
    console.error("speaker output switch failed:", error);
  }
}

export function screenShareSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices?.getDisplayMedia === "function"
  );
}
